/**
 * Pick-pack fulfilment (tidaltech/koi#8081, #8083, #8084), backed by
 * leatherback's FulfillmentService and StockService. Enum values match the
 * backend exactly.
 *
 *   DRAFT -> READY_TO_PICK -> PICKING -> READY_TO_HANDOVER -> COMPLETED
 *   any open state -> CANCELLED
 *
 * The server decides every transition; these helpers only decide what the
 * UI offers.
 */

export const fulfillmentStates = [
  'DRAFT',
  'READY_TO_PICK',
  'PICKING',
  'READY_TO_HANDOVER',
  'COMPLETED',
  'CANCELLED',
] as const;
export type FulfillmentState = (typeof fulfillmentStates)[number];

export const openFulfillmentStates: readonly FulfillmentState[] = [
  'DRAFT',
  'READY_TO_PICK',
  'PICKING',
  'READY_TO_HANDOVER',
];

export const fulfillmentStateLabels: Record<FulfillmentState, string> = {
  DRAFT: 'Draft',
  READY_TO_PICK: 'Ready to pick',
  PICKING: 'Picking',
  READY_TO_HANDOVER: 'Picked',
  COMPLETED: 'Packed',
  CANCELLED: 'Cancelled',
};

export const requirementTypes = ['SERIALIZED', 'STOCK'] as const;
export type RequirementType = (typeof requirementTypes)[number];

export const requirementTypeLabels: Record<RequirementType, string> = {
  SERIALIZED: 'Serialized equipment',
  STOCK: 'Stock',
};

export const overrideReasons = [
  'DAMAGED_BARCODE',
  'MISSING_LABEL',
  'SCANNER_UNAVAILABLE',
  'OTHER',
] as const;
export type OverrideReason = (typeof overrideReasons)[number];

export const overrideReasonLabels: Record<OverrideReason, string> = {
  DAMAGED_BARCODE: 'Barcode damaged or unreadable',
  MISSING_LABEL: 'Label missing',
  SCANNER_UNAVAILABLE: 'Scanner unavailable',
  OTHER: 'Other (explain)',
};

export const stockUnits = ['PIECE', 'METER', 'CENTIMETER', 'GRAM', 'MILLILITER'] as const;
export type StockUnit = (typeof stockUnits)[number];

export const stockUnitLabels: Record<StockUnit, string> = {
  PIECE: 'pcs',
  METER: 'm',
  CENTIMETER: 'cm',
  GRAM: 'g',
  MILLILITER: 'ml',
};

/** Who a stock item is for, from Tilager's vareklasse (intern / forbruk). */
export const stockUsages = ['INTERNAL', 'CONSUMABLE'] as const;
export type StockUsage = (typeof stockUsages)[number];

export const stockUsageLabels: Record<StockUsage, string> = {
  INTERNAL: 'Internal',
  CONSUMABLE: 'Customer consumable',
};

export interface FulfillmentRequirement {
  type: RequirementType;
  label?: string | undefined;
  partIds: string[];
  stockItemId?: string | undefined;
  quantity: number;
}

export interface FulfillmentTemplate {
  id: string;
  name: string;
  description?: string | undefined;
  requirements: FulfillmentRequirement[];
  active: boolean;
  version: number;
  lastModified?: string | undefined;
  lastModifiedBy?: string | undefined;
}

export interface FulfillmentOrderLine {
  id: string;
  requirement: FulfillmentRequirement;
  locationId?: string | undefined;
  itemIds: string[];
  pickedQuantity: number;
  allocationId?: string | undefined;
  /**
   * STOCK lines of an order released short (tidaltech/koi#8511): how much
   * isn't reserved because it wasn't in stock. It is reserved when picked,
   * once received; until then the order can't be finished.
   */
  waitingQuantity?: number | undefined;
}

export interface FulfillmentPick {
  id: string;
  lineId?: string | undefined;
  code: string;
  itemId?: string | undefined;
  quantity: number;
  manualOverride: boolean;
  overrideReason?: OverrideReason | undefined;
  overrideNote?: string | undefined;
  pickedBy?: string | undefined;
  pickedAt?: string | undefined;
  removed: boolean;
  removedBy?: string | undefined;
  removedAt?: string | undefined;
  removeReason?: string | undefined;
}

/**
 * CARRIER: units wait as Ready to ship until carrier evidence moves them.
 * TECHNICIAN: the technician takes them to the site, so they are Delivered
 * there at handover.
 */
export const handoverMethods = ['CARRIER', 'TECHNICIAN'] as const;
export type HandoverMethod = (typeof handoverMethods)[number];

export const amendmentActions = ['REDUCE_QUANTITY', 'SUBSTITUTE', 'CANCEL_LINE'] as const;
export type AmendmentAction = (typeof amendmentActions)[number];

export const amendmentActionLabels: Record<AmendmentAction, string> = {
  REDUCE_QUANTITY: 'Reduced',
  SUBSTITUTE: 'Replaced',
  CANCEL_LINE: 'Cancelled',
};

/** A line changed after release; its reservation was swapped with it. */
export interface FulfillmentLineAmendment {
  lineId?: string | undefined;
  action: AmendmentAction;
  before?: FulfillmentRequirement | undefined;
  after?: FulfillmentRequirement | undefined;
  reason?: string | undefined;
  amendedBy?: string | undefined;
  amendedAt?: string | undefined;
}

export type AmendLineInput = {
  orderId: string;
  version: number;
  lineId: string;
  reason: string;
} & (
  | { action: 'REDUCE_QUANTITY'; quantity: number }
  | { action: 'SUBSTITUTE'; requirement: FulfillmentRequirement; locationId?: string | undefined }
  | { action: 'CANCEL_LINE' }
);

export interface FulfillmentOrder {
  id: string;
  externalReference?: string | undefined;
  customerId: string;
  siteId: string;
  fieldWorkRequestId?: string | undefined;
  shipmentId?: string | undefined;
  templateId?: string | undefined;
  templateVersion?: number | undefined;
  state: FulfillmentState;
  dispatchDate?: string | undefined;
  notes?: string | undefined;
  lines: FulfillmentOrderLine[];
  picks: FulfillmentPick[];
  cancelReason?: string | undefined;
  cancelledBy?: string | undefined;
  cancelledAt?: string | undefined;
  releasedBy?: string | undefined;
  releasedAt?: string | undefined;
  recipient?: string | undefined;
  /** How a completed order left; older orders have none (carrier). */
  handoverMethod?: HandoverMethod | undefined;
  handedOverBy?: string | undefined;
  handedOverAt?: string | undefined;
  createdBy?: string | undefined;
  createTime?: string | undefined;
  version: number;
  lastModified?: string | undefined;
  lastModifiedBy?: string | undefined;
  amendments: FulfillmentLineAmendment[];
}

export interface StockItem {
  id: string;
  sku: string;
  displayName: string;
  description?: string | undefined;
  stockUnit: StockUnit;
  active: boolean;
  // Barcodes printed on the item (EAN/UPC or internal), uppercased without
  // spaces. Scanning one finds the item like its SKU does.
  barcodes: string[];
  // Unset when nobody has said who the item is for.
  usage?: StockUsage | undefined;
  // In stockUnit; 0 means no minimum.
  minimumQuantity: number;
  // Identifies the current photo; unset when there is none. It changes with
  // every new photo, so image URLs include it to avoid stale caches.
  imageId?: string | undefined;
  version: number;
}

export interface WarehouseLocation {
  id: string;
  siteId: string;
  parentId?: string | undefined;
  type: string;
  code: string;
  displayName?: string | undefined;
  active: boolean;
  version: number;
}

export interface StockBalance {
  stockItemId: string;
  locationId: string;
  onHand: number;
  allocated: number;
  available: number;
}

export interface FulfillmentOrderCreateInput {
  customerId: string;
  siteId: string;
  externalReference?: string | undefined;
  fieldWorkRequestId?: string | undefined;
  templateId?: string | undefined;
  dispatchDate?: string | undefined;
  notes?: string | undefined;
  lines: { requirement: FulfillmentRequirement; locationId?: string | undefined }[];
}

/** Only the fields present change; `version` must be the loaded one. */
export interface FulfillmentOrderPatch {
  id: string;
  version: number;
  externalReference?: string | undefined;
  shipmentId?: string | undefined;
  dispatchDate?: string | null | undefined;
  notes?: string | undefined;
  lines?: { requirement: FulfillmentRequirement; locationId?: string | undefined }[] | undefined;
}

export interface FulfillmentTemplateInput {
  name: string;
  description?: string | undefined;
  active: boolean;
  requirements: FulfillmentRequirement[];
}

export interface PickInput {
  orderId: string;
  code: string;
  lineId?: string | undefined;
  quantity?: number | undefined;
  manualOverride?: { reason: OverrideReason; note?: string | undefined } | undefined;
  /** Reused on retries so a scan is never counted twice. */
  idempotencyKey: string;
}

export interface PickResult {
  order: FulfillmentOrder;
  pick: FulfillmentPick;
  /** The server had already recorded this scan; nothing changed. */
  replayed: boolean;
}

/** Stable reasons the backend sends as a "REASON: detail" prefix. */
export const fulfillmentReasons = [
  'UNKNOWN_CODE',
  'WRONG_ITEM',
  'OVER_QUANTITY',
  'DUPLICATE_SCAN',
  'ITEM_ALREADY_ALLOCATED',
  'ITEM_NOT_AVAILABLE',
  'INCOMPLETE',
  'INVALID_STATE',
  'INSUFFICIENT_STOCK',
  'INACTIVE',
  'IDEMPOTENCY_KEY_REUSED',
  'INVALID_REFERENCE',
  'UNKNOWN_REFERENCE',
] as const;
export type FulfillmentReason = (typeof fulfillmentReasons)[number];

/** What an operator is told, without backend identifiers. */
export const fulfillmentReasonMessages: Record<FulfillmentReason, string> = {
  UNKNOWN_CODE: 'That code is not a known serial or stock SKU. Check the label and scan again.',
  WRONG_ITEM: 'That item is not on this order. Put it back.',
  OVER_QUANTITY: 'That is more than the order needs. Put the extra back.',
  DUPLICATE_SCAN: 'That item is already picked for this order.',
  ITEM_ALREADY_ALLOCATED: 'That item is already picked for another open order.',
  ITEM_NOT_AVAILABLE: 'That item is scrapped or needs repair and cannot ship.',
  INCOMPLETE: 'Every line must be fully picked before packing is finished.',
  INVALID_STATE: 'The order is no longer in a state that allows this. Reload it.',
  INSUFFICIENT_STOCK: 'There is not enough available stock to reserve for this order.',
  INACTIVE: 'A stock item, location or template on this order is deactivated.',
  IDEMPOTENCY_KEY_REUSED: 'This scan conflicts with an earlier one. Scan again.',
  INVALID_REFERENCE: 'The order refers to something that does not exist or does not fit.',
  UNKNOWN_REFERENCE: 'A stock item, line or location does not exist or belongs to another site.',
};

export function parseFulfillmentReason(message: string): {
  reason?: FulfillmentReason;
  detail: string;
} {
  const match = /^([A-Z_]+): (.*)$/s.exec(message);
  const reason = match?.[1];
  if (match && reason && (fulfillmentReasons as readonly string[]).includes(reason)) {
    return { reason: reason as FulfillmentReason, detail: match[2] ?? '' };
  }
  return { detail: message };
}

/** The order form number when there is one, else a short id. */
export function orderTitle(order: Pick<FulfillmentOrder, 'id' | 'externalReference'>): string {
  return order.externalReference || `Order ${order.id.slice(0, 8)}`;
}

export function isOpenOrder(order: Pick<FulfillmentOrder, 'state'>): boolean {
  return openFulfillmentStates.includes(order.state);
}

export function canPick(order: Pick<FulfillmentOrder, 'state'>): boolean {
  return (
    order.state === 'READY_TO_PICK' ||
    order.state === 'PICKING' ||
    order.state === 'READY_TO_HANDOVER'
  );
}

/** Released, still open orders take line changes; drafts are edited directly. */
export function canAmendLines(order: Pick<FulfillmentOrder, 'state'>): boolean {
  return canPick(order);
}

export function canRemovePicks(order: Pick<FulfillmentOrder, 'state'>): boolean {
  return order.state === 'PICKING' || order.state === 'READY_TO_HANDOVER';
}

export function linePicked(line: FulfillmentOrderLine): number {
  return line.requirement.type === 'SERIALIZED' ? line.itemIds.length : line.pickedQuantity;
}

export function lineComplete(line: FulfillmentOrderLine): boolean {
  return linePicked(line) >= line.requirement.quantity;
}

/**
 * Progress in units picked. Serialized lines count Items; stock lines count
 * lines rather than units, so 200 cm of cable doesn't swamp one camera.
 */
export function orderProgress(order: Pick<FulfillmentOrder, 'lines'>): {
  complete: number;
  total: number;
} {
  let complete = 0;
  let total = 0;
  for (const line of order.lines) {
    if (line.requirement.type === 'SERIALIZED') {
      total += line.requirement.quantity;
      complete += Math.min(line.itemIds.length, line.requirement.quantity);
    } else {
      total += 1;
      complete += lineComplete(line) ? 1 : 0;
    }
  }
  return { complete, total };
}

export const dispatchGroups = ['overdue', 'today', 'soon', 'later', 'none'] as const;
export type DispatchGroup = (typeof dispatchGroups)[number];

export const dispatchGroupLabels: Record<DispatchGroup, string> = {
  overdue: 'Overdue',
  today: 'Today',
  soon: 'Next 7 days',
  later: 'Later',
  none: 'No dispatch date',
};

/** Groups by dispatch date in the viewer's local calendar. */
export function dispatchGroup(dispatchDate: string | undefined, now: Date): DispatchGroup {
  if (!dispatchDate) return 'none';
  const date = new Date(dispatchDate);
  if (Number.isNaN(date.getTime())) return 'none';
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((day.getTime() - startOfToday.getTime()) / 86_400_000);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 7) return 'soon';
  return 'later';
}

export function requirementLabel(
  requirement: FulfillmentRequirement,
  names: { part?: (id: string) => string; stock?: (id: string) => string } = {},
): string {
  if (requirement.label) return requirement.label;
  if (requirement.type === 'STOCK' && requirement.stockItemId) {
    return names.stock ? names.stock(requirement.stockItemId) : requirement.stockItemId;
  }
  const parts = requirement.partIds.map((id) => (names.part ? names.part(id) : id));
  return parts.join(' or ') || 'Serialized item';
}

export function validateRequirement(requirement: FulfillmentRequirement): string | null {
  if (!Number.isInteger(requirement.quantity) || requirement.quantity <= 0) {
    return 'Quantity must be a whole number above zero.';
  }
  if (requirement.type === 'SERIALIZED' && requirement.partIds.length === 0) {
    return 'Choose at least one part.';
  }
  if (requirement.type === 'STOCK' && !requirement.stockItemId) {
    return 'Choose a stock item.';
  }
  return null;
}
